<template>
	<CardBase 
		ref="card" 
		:app="app"
		:card="card" 
		:loading="loading" 
		:message="message"
		:actions="actions" 
		:guidance="guidance" 
		:status="true" 
		@init="handleInit" 
		@action="handleAction">

		<template #title>
			{{ title }}
		</template>

		<template v-if="isOutage || isDegraded" #status>
			<AtomStatus 
				:level="statusLevel"
				:status="statusMessage" />
		</template>

		<template #content>

			<div class="card-prompt-content">
				<div class="inner">

					<div class="upper">

						<!-- Description -->
						<div class="description">
							<div class="heading">Description</div>

							<v-textarea 
								no-resize
								ref="prompt" 
								rows="4" 
								color="color1" 
								class="textarea" 
								variant="outlined" 
								v-model="card.prompt.user" 
								:hint="hintPrompt"
								:placeholder="placeholderPrompt" 
								@keydown.enter="handleEnter"
								@keydown.stop
								@keypress.stop
								@keyup.stop>

								<template #append-inner>
									<TransitionFade style="height:100%;">
										<div class="actions">
											<v-btn
												elevation="0"
												variant="plain"
												density="comfortable"
												color="secondary" 
												@click="handleHistory">
												<v-tooltip
													activator="parent"
													location="start">
													recent prompts
												</v-tooltip>
												<v-icon icon="fa-solid fa-clock-rotate-left" />
											</v-btn>
											<v-btn
												elevation="0"
												variant="plain"
												density="comfortable"
												color="error" 
												:disabled="card.prompt.user === ''"
												@click="card.prompt.user = ''">
												<v-tooltip
													activator="parent"
													location="start">
													clear prompt
												</v-tooltip>
												<v-icon icon="fa-solid fa-trash" />
											</v-btn>
										</div>
									</TransitionFade>
								</template>

							</v-textarea>

							<PromptGuidance 
								v-if="hasGuidancePrompt"
								:prompt="card.prompt" 
								:custom="card.custom"
								:active="activeGroups" />
						</div>

						<!-- Asset Viewer -->
						<div v-if="hasGuidanceAsset" class="asset hide-on-mobile">
							<div class="heading">{{ categoryCapitalized }}</div>
							<div class="viewer">
								<ViewerImage :asset="primaryAsset" :fileKey="primaryFileKey" />
							</div>
						</div>

					</div>

					<!-- Slider -->
					<div v-if="hasGuidanceInfluence" class="lower">
						<div class="heading">Influence of Sketch</div>
						<!--<div class="heading">
							<span>Influence</span>&nbsp;
							<v-tooltip location="right">
								<template #activator="{ props }">
									<v-icon icon="fa-solid fa-info-circle" v-bind="props"></v-icon>
								</template>
								{{ hintInfluence }}
							</v-tooltip>
						</div>-->
						<div class="card-prompt-content-field slider">
							
							<v-btn-toggle 
								mandatory 
								class="toggle" 
								color="color1" 
								elevation="0" 
								density="default"
								v-model="card.custom.balance">

								<v-btn 
									class="toggle-button" 
									elevation="0"
									:value="0.0">
									<span>Weak</span>
								</v-btn>

								<v-btn 
									class="toggle-button" 
									elevation="0"
									:value="0.5">
									<span>Medium</span>
								</v-btn>

								<v-btn 
									class="toggle-button" 
									elevation="0"
									:value="1.0">
									<span>Strong</span>
								</v-btn>

							</v-btn-toggle>

							<div class="influence-hint">
								{{ hintInfluence }}
							</div>


							<!--<v-slider
								show-ticks="always"
								tick-size="2" 
								class="no-infill-slider" 
								:hint="hintInfluence"
								:min="0"
								:max="1"
								:step="0.05" 
								:thumb-size="30"
								:ticks="ticks" 
								v-model="card.prompt.balance">
							</v-slider>
							-->
						</div>
					</div>

				</div>
			</div>

		</template>

		<template v-if="isExpertMode" #side>

            <SidePanelExpert>
				<div class="expert-item">
                    <v-number-input
                        label="seed"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        v-model="card.custom.seed">
                    </v-number-input>
                </div>
            </SidePanelExpert>

        </template>

	</CardBase>
</template>

<script>
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import LEVELS from '@generio-ai/library-legacy/constants/levels'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { TransitionFade } from '@generio-ai/library-legacy/components/transitions'
import { ViewerImage } from '@generio-ai/library-legacy/components/viewer'
import ACTION from '@/constants/action.js'
import AUGMENTATION from '@/constants/augmentation.js'
import BALANCE from '@/constants/balance.js'
import CARD from '@/constants/card.js'
import PROMPT from '@/constants/prompt.js'
import AtomExplanation from '@/components/common/atom/AtomExplanation.vue'
import AtomStatus from '@/components/common/atom/AtomStatus.vue'
import CardBase from '@/components/flow/cards/CardBase.vue'
import PromptGuidance from '@/components/flow/prompt/PromptGuidance.vue'
import SidePanelExpert from '@/components/flow/sides/SidePanelExpert.vue'
import { base64ToArrayBuffer } from '@/utils/file.js'

export default {
	name: 'CardPrompt',
	components: {
		TransitionFade,
		AtomStatus,
		ViewerImage,
		CardBase,
		PromptGuidance,
		AtomExplanation,
		SidePanelExpert
	},
	props: {
		app: {
            type: String,
            required: true
        },
        card: {
            type: Object,
            required: true
        }
    },
	data() {
		return {
			initialized: false,
			loading: false,
			message: '',
			healthy: {
				text_generate: true,
				image_generate_fromtext: true,
				image_generate_fromimage: true,
				image_generate_fromsketch: true,
				image_generate_fromcomposite: true,
			}
		}
	},
	computed: {
		...mapGetters('auth', [
			'isAdministrator'
		]),
		...mapGetters('user', [
			'isExperimental'
		]),
		...mapGetters('assets', [
            'getFileFromAsset'
        ]),
		...mapGetters('flows', [
			'isExpertMode',
			'isActiveByIndex',
			'isLastByIndex',
			'getPrimaryCardForCard'
		]),

		hasGuidancePrompt() {
			return true 
		},
		hasGuidanceAsset() {
			return this.isCategoryComposite ||
				this.isCategoryPhoto || 
				this.isCategorySketch || 
				this.isCategoryPreview || 
				this.isCategoryModel
		},
		hasGuidanceInfluence() {
			return this.isCategorySketch
		},

		hasPromptUser() {
			return this.promptUser !== null && this.promptUser !== undefined && this.promptUser !== ''
		},
		hasPromptSystem() {
			return this.promptSystem !== null && this.promptSystem !== undefined && this.promptSystem !== ''
		},

		isOutage() {
			return !this.isHealthyImage
		},
        isDegraded() {
            return !this.isHealthyText || !this.isHealthyImage
        },

		isHealthyText() {
			return this.healthy.text_generate
		},
		isHealthyImage() {
			if (this.isCategoryPrimitive) return this.healthy.image_generate_fromtext
			if (this.isCategoryPhoto || this.isCategoryPreview || this.isCategoryModel) return this.healthy.image_generate_fromimage
			if (this.isCategorySketch) return this.healthy.image_generate_fromsketch
			if (this.isCategoryComposite) return this.healthy.image_generate_fromcomposite
			return false
		},

		isActiveCard() {
			return this.isActiveByIndex(this.app, this.index)
		},
		isLastCard() {
            return this.isLastByIndex(this.app, this.index)
        },

		isCategoryPrimitive() {
			return this.category === CATEGORY.PRIMITIVE
		},
		isCategoryPhoto() {
			return this.category === CATEGORY.PHOTO
		},
		isCategorySketch() {
			return this.category === CATEGORY.SKETCH
		},
		isCategoryComposite() {
			return this.category === CATEGORY.COMPOSITE
		},
		isCategoryPreview() {
			return this.category === CATEGORY.PREVIEW
		},
		isCategoryModel() {
			return this.category === CATEGORY.MODEL
		},

		isModification() {
			return this.isCategoryPhoto || this.isCategoryComposite || this.isCategoryPreview
		},

		isPromptTooLong() {
			return this.promptUser?.length > PROMPT.LENGTH_MAX
		},
		isPromptTooVerbose() {
			return this.promptUser?.length > PROMPT.LENGTH_VERBOSE
		},
		isPromptTooPrecise() {
			const regex = /\b\d+(\.\d+)?\s*(mm|cm|m|in|inch|inches|ft|feet|yd|yard|yards)\b/gi
			return regex.test(this.promptUser)
		},

		isInfluenceTooLow() {
			return this.customBalance < 0.2
		},
		isInfluenceTooHigh() {
			return this.customBalance > 0.8
		},
		isInfluencePrimaryDescription() {
			return this.customBalance < 0.5
		},
		isInfluencePrimaryAsset() {
			return this.customBalance > 0.5
		},

		supportsVariant() {
            const baseVersion = this.primaryFileDetails?.version
            if (baseVersion === undefined) return false
            if (baseVersion >= 4) return true
            return false
        },

		statusLevel() {
            if (this.isOutage) return 'error'
            if (this.isDegraded) return 'warning'
            return 'healthy'
        },
        statusMessage() {
            if (this.isOutage) return "Image generation service is currently unavailable."
            if (this.isDegraded) return "Prompt enhancement and translation services are currently unavailable."
            return "Prompt and image generation services are healthy."
        },

		index() {
            return this.card.index
        },
		nextIndex() {
			return this.card.index + 1
		},
		category() {
			return this.card.category
		},
		categoryCapitalized() {
			let category = this.category
			if (category === 'composite') category = 'image'
			return category.charAt(0).toUpperCase() + category.slice(1)
		},
		
		prompt() {
			return this.card.prompt
		},
		promptUser() {
			return this.prompt?.user 
		},
		promptSystem() {
			return this.prompt?.system
		},

		custom() {
			return this.card.custom
		},
		customBalance() {
			return this.custom?.balance
		},
		customSeed() {
			return this.custom?.seed
		},

		primaryCard() {
			return this.getPrimaryCardForCard(this.app, this.card, false)
		},
		primaryAsset() {
			return this.primaryCard?.getSelectedOrFirstAsset()
		},
		primaryFile() {
			return this.getFileFromAsset(this.primaryAsset)
		},
		primaryFileKey() {
			if (this.isCategoryPhoto || this.isCategorySketch || this.isCategoryPreview) return 'default'
			return null
		},
		primaryFileDetails() {
			return this.primaryFile?.details
		},
		activeGroups() {
			if (this.isCategoryModel) return [ 'styles' ]
			return [ 'styles', 'poses', 'views' ]
		},

		title() {
			if (this.isModification) return 'Describe your image edit.'
			if (this.isCategoryModel) return 'Describe the new texture.'
			if (this.isCategorySketch) return 'Describe your sketch.'
			return 'Describe your idea.'
		},
		guidance() {
			if (this.isOutage && !this.isAdministrator) return 'Image generation service is unavailable.'
			if (!this.hasPromptUser) return 'Please provide a description to continue.'
			if (this.isPromptTooLong) return 'Your description is too long.'
			return null
		},
		actions() {
			if (this.isCategoryPrimitive ||
				this.isCategorySketch || 
				this.isCategoryPhoto || 
				this.isCategoryPreview ||
				this.isCategoryComposite || 
				this.isCategoryModel) {
				return [
					ACTION.GENERATE_PREVIEWS
				]
			}

			return []
		},

		placeholderPrompt() {
			if (this.isCategoryPrimitive || this.isCategorySketch) return "green cactus in orange pot"
			if (this.isCategoryPhoto || this.isCategoryComposite || this.isCategoryPreview) return "change the color of the object to red"
			if (this.isCategoryModel) return "silver metallic finish"
			return ""
		},
		hintPrompt() {
			if (this.isPromptTooLong) return 'your description is too long'
			if (this.isPromptTooVerbose) return 'your description is too verbose'
			if (this.isPromptTooPrecise) return 'measurements (mm, cm, etc.) may not be followed strictly'
			if (this.isCategoryPhoto || this.isCategoryComposite || this.isCategoryPreview) return `describe your modification in a few words`
			if (this.isCategoryModel) return `describe the new texture in a few words`
			return 'describe your idea in a few words'
		},
		hintInfluence() {
			//if (this.isInfluenceTooLow) return `${this.category} is not influencing the generation`
			//if (this.isInfluenceTooHigh) return `too strong influence from ${this.category} can affect quality`
			if (this.isInfluencePrimaryDescription) return 'sketch is just a rough guide' //'description is the main influence'
			if (this.isInfluencePrimaryAsset) return 'sketch is followed strictly' //`${this.category} is the main influence`
			return 'sketch is followed fairly closely' //`description and ${this.category} influence the generation equally`
		},

		rules() {
			return [ 
				value => !value || value.length <= PROMPT.LENGTH_MAX || 'Prompt too long' 
			]
		},
		ticks() {
            return {
                0: 'Description',
                0.5: 'Balanced',
                1: this.categoryCapitalized
            }
        }
	},
	watch: {
		promptUser(newValue, oldValue) {
			if (newValue === oldValue) return
			this.$refs.card?.reset()
			this.removeCard({
				app: this.app, 
				index: this.nextIndex
			})
		}
    },
	methods: {
		...mapActions('publics', [
			'fetchStatus'
		]),
		...mapActions('dialogs', [
			'open'
		]),
		...mapActions('notifications', [
			'show'
		]),
		...mapActions('flows', [
			'removeCard',
			'newTransaction',
			'commitPrimitiveAsset',
			'fetchBase',
			'fetchGenerateImages',
			'intermediateGenerateText'
		]),
		focus() {
			if (!this.initialized) return
			if (this.$refs.prompt && !this.hasPromptUser) 
				this.$refs.prompt.focus({ preventScroll: true })
		},
		async updateStatus() 
		{
			// default is that the services are healthy
			this.healthy = {
				text_generate: true,
				image_generate_fromtext: true,
				image_generate_fromimage: true,
				image_generate_fromsketch: true,
				image_generate_fromcomposite: true,
			}

			try
			{
				// get status
				const status = await this.fetchStatus()

				// has healthy services
				if (status?.healthy) 
				{
					// text
					const text = status.healthy['text_generate']
					if (text === false) this.healthy.text_generate = false

					// image generate from text
					const imageFromText = status.healthy['image_generate_fromtext']
					if (imageFromText === false) this.healthy.image_generate_fromtext = false

					// image generate from image
					const imageFromImage = status.healthy['image_generate_fromimage']
					if (imageFromImage === false) this.healthy.image_generate_fromimage = false

					// image generate from sketch
					const imageFromSketch = status.healthy['image_generate_fromsketch']
					if (imageFromSketch === false) this.healthy.image_generate_fromsketch = false

					// image generate from composite
					const imageFromComposite = status.healthy['image_generate_fromcomposite']
					if (imageFromComposite === false) this.healthy.image_generate_fromcomposite = false
				}
			}
			catch (error)
			{
				// silent fail - if status check does not work, we still want to proceed
			}
		},
		handleInit(assets, isNew) {
			if (isNew) {
				this.card.custom ??= {}
				this.card.custom.augmentation = {
					styles: [...AUGMENTATION.DEFAULT.styles],
					poses:  [...AUGMENTATION.DEFAULT.poses],
					views:  [...AUGMENTATION.DEFAULT.views],
				}
				// only set defaults when undefined
				this.card.custom.balance ??= BALANCE.DEFAULT
				this.card.custom.seed ??= -1
			}
			this.initialized = true
			this.focus()
		},
		async handleAction(action) {
			if (action === null || action === undefined) return

			// set count
			const count = 3

			// always ensure we perform enhancement first
			const [invalid, prompts] = await this.handleEnhance(count)
			if (invalid) return

			switch(action.id) 
			{
				case ACTION.GENERATE_PREVIEWS.id:
					this.newTransaction({
						app: this.app,
						card: this.card,
						taskCommit: this.commitPrimitiveAsset(),
						taskFetch: this.fetchGenerateImages({
							component: CARD.SELECT_ASSET,
							count: count,
							prompts: prompts,
							seed: this.customSeed,
							preload: false
						})
					})
					break
			}
		},
		async handleEnhance(count) 
		{
			// little helper
			const repeat = prompt => Array.from({ length: count }, () => prompt)

			// ensure system prompt exists
			this.card.prompt.system = this.promptUser

			// only enhance if available
			if (!this.healthy.text_generate) return [ false, repeat(this.promptUser) ]

			// user feedback
			this.loading = true
			this.message = 'analyzing prompt'

			try 
			{
				// enhance prompt
				const response = await this.intermediateGenerateText({
					app: this.app,
					card: this.card,
					parentCard: null,
					count: count,
					prompt: this.promptUser,
					template: 'enhance_prompt', 
					ignore: true,
					preload: true
				})

				// get response details
				const textAsset = response.assets[0]
				const textData = textAsset?.files['default']?.data

				// parse file as json
				const text = JSON.parse(new TextDecoder().decode(base64ToArrayBuffer(textData)))

				// check if text contains prompts
				if (text == null || 
					text.output == null || 
					text.output.prompts == null ||
					text.output.prompts.length === 0
				) {
					return [ false, repeat(this.promptUser) ]
				}

				// handle nsfw content
				if (text.output.nsfw) {
					this.show({
						message: 'Your prompt was flagged as inappropriate. Please modify it and try again.',
						level: LEVELS.ERROR
					})
					return [ true, null ]
				}

				// handle enahnced (and translated) prompts
				const prompts = text.output.prompts
				if (prompts) return [ false, prompts ]
			}
			catch (error) 
			{
				// silent fail - if enhancement does not work, we still want to proceed
				console.log('text generate error:', error)
			} 
			finally 
			{
				// stop loading
				this.loading = false
			}
			// fallback
			return [ false, repeat(this.promptUser) ]
		},
		handleHistory() {
			this.open({
                component: 'history',
				payload: {
                    card: this.card
                }
            })
		},
		handleEnter(event) {
			event.target.blur()

			// try to pick the “generate previews” action if available
			const previewAction = this.actions.find(
				a => a.id === ACTION.GENERATE_PREVIEWS.id
			)

			// fall back to whatever the first action is
  			const action = previewAction || this.actions[0]

			if (action) {
				const card = this.$refs.card
				card.handleAction(action)
			}
		}
	},
	mounted() {
		this.updateStatus()
	}
}
</script>

<style>
.textarea .v-field {
	border-radius: 8px;
	background-color: rgb(var(--v-theme-darkbase));
}

.textarea .v-field__append-inner {
	padding-top: 0px;
}

.textarea .v-field__input {
	font-size: 1.2rem !important;
}

.textarea .v-input__details {
	padding: 0px;
}

.textarea .v-messages {
	margin-top: 10px !important;
	font-size: 1.0rem !important;
}

.slider .v-messages {
	margin-top: 12px !important;
	font-size: 1.0rem !important;
}
</style>

<style scoped>
.card-prompt-content {
	width: 100%;
	height: 100%;
	margin: 0px;
	padding: 0px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
}

.card-prompt-content .inner {
	width: 100%;
	flex-shrink: 1;
	padding: 2rem;
	gap: 1rem;
	display: flex;
	flex-direction: column;
}

.card-prompt-content .inner .upper {
	width: 100%;
	display: flex;
	flex-direction: row;
	gap: 2rem;
}

.card-prompt-content .inner .lower {
	flex-shrink: 1;
}

.card-prompt-content .inner .upper .description {
	flex-grow: 1;
}

.card-prompt-content .inner .upper .description .textarea .actions {
	height: 100%;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	align-items: center;
	padding-top: 10px;
	padding-bottom: 10px;
}

.card-prompt-content .inner .upper .description .textarea .actions .v-btn {
	max-height: 40px;
	min-height: 40px;
	max-width: 40px;
	min-width: 40px;
}

.card-prompt-content .inner .upper .asset {
	flex-shrink: 1;
	width: 25%;
	max-width: 300px;
}

.card-prompt-content .inner .upper .asset .viewer {
	position: relative;
	display: block;
	width: 100%;
	height: auto;
	aspect-ratio: 1 / 1;
	border-radius: 8px;
	overflow: hidden;
	border: 1px solid rgb(var(--v-theme-lightbase));
}

.card-prompt-content .heading {
	margin-bottom: 0.5rem;
	text-align: center; 
	user-select: none;
	font-size: 1.3rem;
}

.card-prompt-content .toggle {
	width: 80%;
	border: 1px solid rgb(var(--v-theme-lightbase));
}

.card-prompt-content .toggle-button {
	flex: 1;
	background-color: rgb(var(--v-theme-darkbase));
	border-radius: 0px !important;
}

.card-prompt-content .influence-hint {
	margin-top: 0.5rem;
	font-size: 1rem;
	color: rgb(var(--v-theme-secondary));
}

@media (max-width: 800px) {
	.card-prompt-content .heading {
		margin-bottom: 0.2rem;
		font-size: 1.1rem;
	}

	.card-prompt-content .inner {
		padding: 1rem;
	}

	.card-prompt-content .toggle {
		width: 100%;
	}

	.card-prompt-content .influence-hint {
		display: none;
	}
}

@media (max-width: 400px) {
	.card-prompt-content .inner {
		padding: 0.2rem;
	}
}
</style>